import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/landing/Header";
import { submitReview } from "@/lib/api";
import { TRIP_TYPES } from "@/data/siteData";

export default function ReviewSubmitPage() {
    const [form, setForm] = useState({ name: "", city: "", trip_type: "", rating: 5, text: "" });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.city.trim() || !form.text.trim()) {
            toast.error("Please fill your name, city and review.");
            return;
        }
        setLoading(true);
        try {
            await submitReview(form);
            setDone(true);
        } catch {
            toast.error("Could not submit right now. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const inputCls = "w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-saffron";
    const labelCls = "block text-[11px] font-bold uppercase tracking-wider text-stone-500 mb-1";

    return (
        <div className="min-h-screen bg-cream" data-testid="review-page">
            <header className="border-b border-stone-300/70">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
                    <Logo />
                    <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy hover:text-saffron" data-testid="back-home-link">
                        <ArrowLeft className="h-4 w-4" /> Back to site
                    </Link>
                </div>
            </header>

            <main className="max-w-xl mx-auto px-4 py-14">
                {done ? (
                    <div className="bg-white rounded-2xl border border-stone-300 p-10 text-center" data-testid="review-success">
                        <CheckCircle2 className="h-14 w-14 text-saffron mx-auto" />
                        <h1 className="mt-4 font-display font-black text-2xl text-navy">Dhanyavaad!</h1>
                        <p className="mt-2 text-sm text-stone-600 leading-relaxed">
                            Thanks for sharing your experience. Our team will review it shortly, and it'll appear on the
                            GhumneChaley wall once approved.
                        </p>
                        <Link to="/" className="mt-6 inline-block px-7 py-3 rounded-full bg-saffron text-white text-xs font-display font-black uppercase tracking-wider hover:bg-saffron-dark" data-testid="review-success-home">
                            Back to GhumneChaley.com
                        </Link>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-stone-300 p-6 sm:p-8 shadow-[8px_8px_0_0_#1B3A66]">
                        <p className="font-script text-3xl text-saffron -rotate-1 inline-block">aapki kahani</p>
                        <h1 className="mt-1 font-display font-black text-2xl sm:text-3xl text-navy">Share Your Trip Experience</h1>
                        <p className="mt-2 text-sm text-stone-500">Travelled with GhumneChaley? Tell India how it went.</p>

                        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="review-form">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls} htmlFor="rv-name">Your Name *</label>
                                    <input id="rv-name" data-testid="review-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={set("name")} />
                                </div>
                                <div>
                                    <label className={labelCls} htmlFor="rv-city">City *</label>
                                    <input id="rv-city" data-testid="review-city-input" className={inputCls} placeholder="e.g. Pune" value={form.city} onChange={set("city")} />
                                </div>
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="rv-type">Trip Type</label>
                                <select id="rv-type" data-testid="review-type-select" className={inputCls} value={form.trip_type} onChange={set("trip_type")}>
                                    <option value="">Select trip type</option>
                                    {TRIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>Your Rating *</label>
                                <div className="flex gap-1.5" data-testid="review-stars-input">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <button type="button" key={n} onClick={() => setForm((f) => ({ ...f, rating: n }))} data-testid={`review-star-${n}`} aria-label={`${n} star`}>
                                            <Star className={`h-8 w-8 transition-colors ${n <= form.rating ? "fill-gold text-gold" : "text-stone-300"}`} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="rv-text">Your Review *</label>
                                <textarea id="rv-text" data-testid="review-text-input" rows={4} className={inputCls} placeholder="How was the trip? What did the team handle well?" value={form.text} onChange={set("text")} />
                            </div>
                            <button type="submit" disabled={loading} data-testid="review-submit-btn" className="w-full rounded-full bg-saffron hover:bg-saffron-dark text-white font-display font-black text-sm uppercase tracking-wider py-3.5 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                                Submit Review
                            </button>
                        </form>
                    </div>
                )}
            </main>
        </div>
    );
}
